import { useEffect, useRef, useState } from 'react'

import { Controller, useFormContext } from 'react-hook-form'

import type { MediaSelection } from '@/modules/message/components/editor/fields/message-media-dropdown'
import type { MessageMediaSelectorProps } from '@/modules/message/components/editor/fields/message-media-selector'
import { MessageMediaSelector } from '@/modules/message/components/editor/fields/message-media-selector'
import { MessagePriceSelector } from '@/modules/message/components/editor/fields/message-price-selector'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context.tsx'
import { classNames } from '@/utils/helpers'

type Props = Omit<MessageMediaSelectorProps, 'field'>

export const MessageMediaSelectorController = (props: Props) => {
  const { className, ...rest } = props
  const { watch, setValue } = useFormContext()
  const { defaultValues } = useMessageEditor()

  const medias = watch('medias') as MediaSelection[] | null | undefined
  const price = watch('price')
  const initialPrice = defaultValues.price
  const initialMedias = defaultValues.medias

  const [mediaWasChanged, setMediaWasChanged] = useState(false)
  const lastKnownMedias = useRef(initialMedias)

  useEffect(() => {
    if (lastKnownMedias.current?.length !== medias?.length) {
      setMediaWasChanged(true)
    }
    lastKnownMedias.current = medias

    /*
    This is to prevent resetting the price when the user already had a price, but not any medias.
    If the user had a price and no media, the price should not be reset, but if he changed the media to zero, then the price should be reset.
     */
    if (mediaWasChanged && !medias?.length && price > 0) {
      setValue('price', 0)
    }
  }, [medias, setValue, price, mediaWasChanged, setMediaWasChanged])

  return (
    <Controller
      name="medias"
      render={({ field, fieldState: { error } }) => {
        const canSelectPrice =
          !!medias?.length || (initialPrice > 0 && price > 0)

        return (
          <div
            {...rest}
            className={classNames('grid grid-cols-2 gap-[24px]', className)}
          >
            <MessageMediaSelector
              className={classNames(
                canSelectPrice ? 'col-span-1' : 'col-span-2',
              )}
              field={{ ...field, error }}
            />
            {canSelectPrice && (
              <Controller
                name="price"
                render={({ field, fieldState: { error } }) => (
                  <MessagePriceSelector
                    {...props}
                    className="col-span-1"
                    field={{ ...field, error }}
                  />
                )}
              />
            )}
          </div>
        )
      }}
    />
  )
}
